import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";

/**
 * Highlighting by a saved view (a combined watchlist): its colour, and when it
 * matches.
 *
 * A saved view is a matching rule, so there is nothing about it that restricts
 * it to filtering — "mark anything matching Fed Watch" is as valid as "show
 * only Fed Watch". Both feed families carry a client-side evaluator for a
 * CategoryQuery (headlineFilterMatcher / firehoseFilterMatcher); rows are
 * matched through highlightMatches.ts, with watchlists, in the widget's order.
 */

/**
 * The stand-in colour for a saved view nobody has picked one for.
 *
 * Views gained a stored colour later (CategoryQuery.color); until one is set,
 * one is derived from the id: stable across sessions and distinguishable when
 * several views are highlighted at once.
 *
 * Chosen to read on the near-black feed with black text, and to stay clear of
 * the red a new watchlist defaults to.
 */
export const VIEW_COLORS = [
  "#00AEEF", // squawk blue
  "#F5A623", // amber
  "#7ED321", // green
  "#BD10E0", // magenta
  "#50E3C2", // teal
  "#FFD400", // yellow
];

export function derivedViewColor(viewId: number | undefined): string {
  if (viewId === undefined) return VIEW_COLORS[0];
  return VIEW_COLORS[Math.abs(viewId) % VIEW_COLORS.length];
}

/** A view's highlight colour: the one picked for it, else the derived one. */
export function savedViewColor(
  view: Pick<CategoryQuery, "id" | "color">
): string {
  return view.color || derivedViewColor(view.id);
}

/**
 * A starting colour for a new combined watchlist, before it has an id.
 *
 * Steps through the same palette by how many the user already has, so a run
 * of new lists doesn't all come out the same colour.
 */
export function nextViewColor(existingCount: number): string {
  return VIEW_COLORS[Math.abs(existingCount) % VIEW_COLORS.length];
}

/**
 * First view, in the order given, whose rule `item` meets. Rows are
 * highlighted through highlightMatches.ts; this serves the alerts.
 *
 * An empty view matches everything (see headlineFilterMatcher). As a highlight
 * that would paint the whole feed, and as an alert it would sound for every
 * story, so it never matches here.
 */
export function firstMatchingView<T>(
  item: T,
  views: CategoryQuery[],
  matcher: (item: T, view: CategoryQuery) => boolean
): CategoryQuery | undefined {
  return views.find((view) => !isEmptyView(view) && matcher(item, view));
}

export function isEmptyView(view: CategoryQuery): boolean {
  return (
    (view.categories?.length ?? 0) === 0 &&
    !view.searchKeyword?.trim() &&
    (view.portfolios?.length ?? 0) === 0 &&
    (view.tagsWatchlists?.length ?? 0) === 0
  );
}

